import type { CircleLayerSpecification, SymbolLayerSpecification, Map as MapboxMap } from "mapbox-gl";

import { makeAutoObservable } from "mobx";
import { MapBoxSource } from "./MapBoxSource";
import { MapBoxLayer } from "./MapBoxLayer";
import type { LngLatTuple } from "./MarkersStore";
import type { ClusterConfig } from "./MapStore";

export class ClusterMarkersStore {
	private source: MapBoxSource;
	private shadowLayer: MapBoxLayer<CircleLayerSpecification>;
	private clustersLayer: MapBoxLayer<CircleLayerSpecification>;
	private countLayer: MapBoxLayer<SymbolLayerSpecification>;
	private cfg: Required<ClusterConfig>;

	constructor(source: MapBoxSource, config: ClusterConfig = {}) {
		this.source = source;
		this.cfg = {
			sourceId: source.id,
			clusterRadius: config.clusterRadius ?? 30,
			clusterMaxZoom: config.clusterMaxZoom ?? 11,
			clusterLayerId: config.clusterLayerId ?? "clusters-layer",
			clusterCountLayerId: config.clusterCountLayerId ?? "clusters-count-layer",
		};

		const shadowLayerSpec: CircleLayerSpecification = {
			id: `${this.cfg.clusterLayerId}-shadow`,
			type: "circle",
			source: this.source.id,
			filter: ["has", "point_count"],
			paint: {
				"circle-color": "#000000",
				"circle-opacity": 0.12,
				"circle-blur": 0.6,
				"circle-radius": ["step", ["get", "point_count"], 16, 25, 20, 100, 24],
				"circle-translate": [0, 2],
			},
		};

		const clustersLayerSpec: CircleLayerSpecification = {
			id: this.cfg.clusterLayerId,
			type: "circle",
			source: this.source.id,
			filter: ["has", "point_count"],
			paint: {
				"circle-color": "#6366F1",
				"circle-opacity": 0.92,
				"circle-radius": ["step", ["get", "point_count"], 14, 25, 18, 100, 22],
				"circle-stroke-width": 2.5,
				"circle-stroke-color": "#ffffff",
				"circle-stroke-opacity": 0.9,
			},
		};

		const countLayerSpec: SymbolLayerSpecification = {
			id: this.cfg.clusterCountLayerId,
			type: "symbol",
			source: this.source.id,
			filter: ["has", "point_count"],
			layout: {
				"text-field": ["get", "point_count_abbreviated"],
				"text-size": 12,
			},
			paint: { "text-color": "#ffffff" },
		};

		this.shadowLayer = new MapBoxLayer({ id: shadowLayerSpec.id, spec: shadowLayerSpec });
		this.clustersLayer = new MapBoxLayer({ id: clustersLayerSpec.id, spec: clustersLayerSpec });
		this.countLayer = new MapBoxLayer({ id: countLayerSpec.id, spec: countLayerSpec });

		makeAutoObservable(this);
	}

	attach(map: MapboxMap) {
		this.source.attach(map);
		this.shadowLayer.attach(map);
		this.clustersLayer.attach(map);
		this.countLayer.attach(map);

		// Click a cluster → zoom in to the level at which it would expand.
		this.clustersLayer.onClick(
			({ feature, lng, lat }) => {
				const clusterId = feature.properties?.cluster_id as number | undefined;
				if (clusterId == null) return;
				const geoSource = map.getSource(this.source.id) as unknown as {
					getClusterExpansionZoom: (clusterId: number, cb: (err: unknown, zoom: number) => void) => void;
				};
				geoSource.getClusterExpansionZoom(clusterId, (error, zoom) => {
					if (error) return;
					map.easeTo({ center: [lng, lat], zoom });
				});
			},
			{ idProp: "cluster_id" },
		);

		// Pointer cursor over clusters, like the original.
		this.clustersLayer.onMouseEnter(({ map: cursorMap }) => {
			cursorMap.getCanvas().style.cursor = "pointer";
		});
		this.clustersLayer.onMouseLeave(({ map: cursorMap }) => {
			cursorMap.getCanvas().style.cursor = "";
		});
	}

	detach() {
		this.shadowLayer.detach();
		this.clustersLayer.detach();
		this.countLayer.detach();
	}

	setMarkers<TData>(
		data: TData[],
		getCoordinates: (item: TData) => LngLatTuple,
		getId: (item: TData) => string,
		getProperties?: (item: TData) => Record<string, unknown>,
	) {
		const fc: GeoJSON.FeatureCollection = {
			type: "FeatureCollection",
			features: data.map(item => {
				const [lng, lat] = getCoordinates(item);
				return {
					type: "Feature" as const,
					properties: {
						id: getId(item),
						...(getProperties ? getProperties(item) : {}),
					},
					geometry: {
						type: "Point" as const,
						coordinates: [lng, lat],
					},
				};
			}),
		};

		this.source.setGeoJSONData(fc);
	}
}
